 
/* eslint-disable @typescript-eslint/no-unused-vars */
import React, { useState, useEffect } from 'react';
import { LineChart, BarChart2, TrendingUp, Users, DollarSign, ShoppingCart, Activity, Building2, Factory, Truck, Store, Calendar, Globe, TrendingDown } from 'lucide-react';
import { formatCurrency } from '@smartphone-tycoon/shared-ui';
import { calculateMonthlyRevenue, calculateProfitMargin } from '../../../utils/businessCalculations';
import { useInventoryStore } from '../../../stores/inventoryStore';
import type { Product, Employee } from '@smartphone-tycoon/shared-core';
import type { CompanyState } from '../../../types/company';
import type { ResearchState } from '@smartphone-tycoon/shared-core';
import { PlaceholderText } from '../../ui/Placeholder';

interface ReportsProps {
  products: Product[];
  companyState: CompanyState;
  employees?: Employee[];
  researchState?: ResearchState;
}

export const Reports: React.FC<ReportsProps> = ({ products, companyState, employees = [], researchState }) => {
  // Get inventory data from store (must be called before any conditional returns)
  const components = useInventoryStore(state => state.components);
  const selectLowStockComponents = useInventoryStore(state => state.selectLowStockComponents);

  // Handle undefined products/companyState
  const safeProducts = products ?? [];

  // Handle loading state
  if (!companyState) {
    return (
      <div className="p-6">
        <div className="text-center text-theme-text-muted py-8">
          Loading reports data...
        </div>
      </div>
    );
  }
  
  // Calculate inventory metrics
  const inventory = {
    totalComponents: Array.from(components.values()).reduce((sum, comp) => sum + comp.currentStock, 0),
    lowStockCount: selectLowStockComponents().length,
    supplyCost: Array.from(components.values()).reduce((sum, comp) => sum + (comp.currentStock * comp.unitCost), 0)
  };

  // Calculate aggregate costs
  const calculateAggregateCosts = () => {
    const developmentStatuses = ['concept', 'design', 'development', 'manufacturing'];
    const developingProducts = safeProducts.filter(p => developmentStatuses.includes(p.status));
    const activeProducts = safeProducts.filter(p => p.status === 'production');
    
    // Calculate monthly production costs
    const totalMonthlyProductionCost = activeProducts.reduce((sum, product) => {
      const dailySalesData = companyState.dailyStats?.productSales?.find(ps => ps.productId === product.id);
      const dailyUnitsSold = dailySalesData?.unitsSold || 0;
      const dailyProductionCost = dailyUnitsSold * product.productionCost;
      return sum + (dailyProductionCost * 30);
    }, 0);
    
    // Calculate component inventory value
    const totalComponentCosts = safeProducts.reduce((sum, product) => {
      const components = product.designConfig?.components || {};
      let componentCost = 0;
      Object.values(components).forEach((component) => {
        if (typeof component === 'object' && component !== null && 'cost' in component) {
          componentCost += (component as { cost: number }).cost || 0;
        }
      });
      return sum + componentCost;
    }, 0);
    
    // R&D costs estimate (placeholder)
    const rdCostPerProduct = 50000; // TODO: Replace with actual R&D costs
    const totalRDCosts = developingProducts.length * rdCostPerProduct;
    
    return {
      totalMonthlyProductionCost,
      totalComponentCosts,
      totalRDCosts,
      activeProducts: activeProducts.length,
      developingProducts: developingProducts.length
    };
  };

  const aggregateCosts = calculateAggregateCosts();

  // Calculate Operations metrics
  const calculateOperationsMetrics = () => {
    const activeProducts = safeProducts.filter(p => p.status === 'production');
    const hasActiveProducts = activeProducts.length > 0;
    
    // Financial metrics
    const monthlyRevenue = hasActiveProducts ? calculateMonthlyRevenue(companyState.marketShare, companyState.reputation) : 0;
    const profitMargin = hasActiveProducts ? calculateProfitMargin(monthlyRevenue) : 0;
    const monthlySales = hasActiveProducts ? Math.round(50000 * companyState.marketShare) : 0;
    const growthRate = hasActiveProducts ? Math.round((companyState.marketShare * 100 - 10) * 2) / 2 : 0;
    
    // Company valuation
    const companyValue = Math.round(companyState.cash * (1 + companyState.marketShare * 5));
    
    // Operations costs
    const dailySalaryCost = employees.reduce((sum, emp) => sum + emp.salary, 0);
    const monthlySalaryCost = dailySalaryCost * 30;
    const overheadCost = Math.ceil(employees.length / 10) * 5000 + employees.length * 450; // Office + utilities + equipment + insurance
    const monthlyOperationsCost = monthlySalaryCost + overheadCost;
    
    return {
      monthlyRevenue,
      profitMargin,
      monthlySales,
      growthRate,
      companyValue,
      monthlyOperationsCost,
      monthlySalaryCost,
      overheadCost,
      employeeCount: employees.length
    };
  };

  const operationsMetrics = calculateOperationsMetrics();

  // Calculate Research metrics
  const calculateResearchMetrics = () => {
    if (!researchState) {
      return {
        researchPoints: 0,
        monthlyRD: 0,
        rpGeneration: 0,
        costReduction: 0,
        efficiency: 0,
        monthlyRDInvestment: 0,
        researchPointsSpent: 0,
        costEfficiency: 0
      };
    }

    const monthlyRDInvestment = researchState.monthlyInvestment || 0;
    const baseRPRate = 0.001;
    const monthlyRPFromInvestment = (monthlyRDInvestment * baseRPRate) * (researchState.rpGenerationRate || 1);
    const monthlyRPFromProducts = employees.length > 0 ? Math.floor((companyState.reputation / 10) * safeProducts.filter(p => p.status === 'production').length) * 30 : 0;
    const totalMonthlyRP = monthlyRPFromInvestment + monthlyRPFromProducts;
    
    const unlockedNodes = researchState.unlockedNodes || [];
    const totalRPSpent = unlockedNodes.length * 100; // Estimate
    const costEfficiency = monthlyRDInvestment > 0 ? monthlyRDInvestment / Math.max(1, monthlyRPFromInvestment) : 0;

    return {
      researchPoints: researchState.researchPoints || 0,
      monthlyRD: monthlyRDInvestment,
      rpGeneration: totalMonthlyRP,
      costReduction: unlockedNodes.length * 5, // TODO: Make tech bonus configurable
      efficiency: Math.min(100, unlockedNodes.length * 3), // TODO: Make efficiency bonus configurable
      monthlyRDInvestment,
      researchPointsSpent: totalRPSpent,
      costEfficiency
    };
  };

  const researchMetrics = calculateResearchMetrics();

  // Calculate Marketing metrics
  const calculateMarketingMetrics = () => {
    const monthlyRevenue = operationsMetrics.monthlyRevenue;
    const monthlyMarketingBudget = monthlyRevenue * 0.15; // TODO: Make marketing percentage configurable
    const marketingEfficiency = monthlyMarketingBudget > 0 ? monthlyRevenue / monthlyMarketingBudget : 0;
    const brandValue = companyState.reputation * 1000; // TODO: Make brand value multiplier configurable

    return {
      monthlyMarketingBudget,
      marketingEfficiency,
      brandValue
    };
  };

  const marketingMetrics = calculateMarketingMetrics();

  // Calculate Finance metrics
  const calculateFinanceMetrics = () => {
    const monthlyRevenue = operationsMetrics.monthlyRevenue;
    const monthlyCosts = operationsMetrics.monthlyOperationsCost + researchMetrics.monthlyRDInvestment + marketingMetrics.monthlyMarketingBudget;
    const netProfit = monthlyRevenue - monthlyCosts;
    const profitMargin = monthlyRevenue > 0 ? (netProfit / monthlyRevenue) * 100 : 0;

    return {
      monthlyRevenue,
      profitMargin,
      monthlyCosts,
      netProfit
    };
  };

  const financeMetrics = calculateFinanceMetrics();

  return (
    <div className="p-6 space-y-6 h-full overflow-auto">
      {/* Product Section */}
      <div className="space-y-4">
        <h3 className="text-lg font-semibold text-theme-text-secondary">Product</h3>
        
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="bg-theme-bg-secondary border-l-4 border-theme-accent p-4 rounded-lg">
            <h3 className="font-semibold text-theme-text-primary mb-2">Monthly Production Costs</h3>
            <p className="text-2xl font-bold text-theme-text-primary">
              {formatCurrency(aggregateCosts.totalMonthlyProductionCost)}
            </p>
            <p className="text-sm text-theme-text-secondary mt-1">
              From {aggregateCosts.activeProducts} active products
            </p>
          </div>
          
          <div className="bg-theme-bg-secondary border-l-4 border-theme-border p-4 rounded-lg">
            <h3 className="font-semibold text-theme-text-primary mb-2">Component Inventory</h3>
            <p className="text-2xl font-bold text-theme-text-primary">
              {formatCurrency(aggregateCosts.totalComponentCosts)}
            </p>
            <p className="text-sm text-theme-text-secondary mt-1">
              Total component value
            </p>
          </div>
          
          <div className="bg-theme-bg-secondary border-l-4 border-theme-success p-4 rounded-lg">
            <h3 className="font-semibold text-theme-text-primary mb-2">Active R&D Investment</h3>
            <p className="text-2xl font-bold text-theme-text-primary">
              {formatCurrency(aggregateCosts.totalRDCosts)}
            </p>
            <p className="text-sm text-theme-text-secondary mt-1">
              {aggregateCosts.developingProducts} products in development
            </p>
          </div>
        </div>

        {/* Component Stats from ComponentManager */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="bg-theme-bg-secondary border-l-4 border-theme-accent p-4 rounded-lg">
            <div className="flex items-center gap-2 mb-2">
              <h3 className="font-semibold text-theme-text-primary">Total Components</h3>
            </div>
            <div className="text-2xl font-bold text-theme-text-primary">
              {inventory.totalComponents.toLocaleString()}
            </div>
            <p className="text-sm text-theme-text-secondary">In Stock</p>
          </div>

          <div className="bg-theme-bg-secondary border-l-4 border-theme-error p-4 rounded-lg">
            <div className="flex items-center gap-2 mb-2">
              <h3 className="font-semibold text-theme-text-primary">Low Stock Alerts</h3>
            </div>
            <div className="text-2xl font-bold text-theme-text-primary">
              {inventory.lowStockCount}
            </div>
            <p className="text-sm text-theme-text-secondary">Components Below Threshold</p>
          </div>

          <div className="bg-theme-bg-secondary border-l-4 border-theme-success p-4 rounded-lg">
            <div className="flex items-center gap-2 mb-2">
              <h3 className="font-semibold text-theme-text-primary">Supply Chain Cost</h3>
            </div>
            <div className="text-2xl font-bold text-theme-text-primary">
              {formatCurrency(inventory.supplyCost)}
            </div>
            <p className="text-sm text-theme-text-secondary">Monthly Average</p>
          </div>
        </div>

        {/* Market Statistics */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mt-4">
          <div className="bg-theme-bg-secondary border-l-4 border-theme-success p-4 rounded-lg">
            <div className="flex items-center gap-2 mb-2">
              <LineChart className="w-5 h-5 text-theme-text-secondary" />
              <h3 className="font-semibold text-theme-text-primary">Market Trends</h3>
            </div>
            <div className="text-2xl font-bold text-theme-text-primary">
              <PlaceholderText text="5.2%" />
            </div>
            <p className="text-sm text-theme-text-secondary">Monthly Growth Rate</p>
          </div>

          <div className="bg-theme-bg-secondary border-l-4 border-theme-accent p-4 rounded-lg">
            <div className="flex items-center gap-2 mb-2">
              <BarChart2 className="w-5 h-5 text-theme-text-secondary" />
              <h3 className="font-semibold text-theme-text-primary">Market Share</h3>
            </div>
            <div className="text-2xl font-bold text-theme-text-primary">
              {((companyState.marketShare || 0) * 100).toFixed(1)}%
            </div>
            <p className="text-sm text-theme-text-secondary">Your Current Share</p>
          </div>

          <div className="bg-theme-bg-secondary border-l-4 border-theme-border p-4 rounded-lg">
            <div className="flex items-center gap-2 mb-2">
              <TrendingUp className="w-5 h-5 text-theme-text-secondary" />
              <h3 className="font-semibold text-theme-text-primary">Price Range</h3>
            </div>
            <div className="text-2xl font-bold text-theme-text-primary">
              <PlaceholderText text={`${formatCurrency(699)} - ${formatCurrency(1299)}`} />
            </div>
            <p className="text-sm text-theme-text-secondary">Optimal Price Range</p>
          </div>

          <div className="bg-theme-bg-secondary border-l-4 border-theme-warning p-4 rounded-lg">
            <div className="flex items-center gap-2 mb-2">
              <Users className="w-5 h-5 text-theme-text-secondary" />
              <h3 className="font-semibold text-theme-text-primary">Market Saturation</h3>
            </div>
            <div className="text-2xl font-bold text-theme-text-primary">
              <PlaceholderText text="68%" />
            </div>
            <p className="text-sm text-theme-text-secondary">Current Saturation Level</p>
          </div>
        </div>
      </div>

      {/* Operations Section */}
      <div className="space-y-4 mt-6">
        <h3 className="text-lg font-semibold text-theme-text-secondary">Operations</h3>
        
        {/* Financial Metrics */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          <div className="bg-theme-bg-secondary border-l-4 border-theme-success p-4 rounded-lg">
            <div className="flex items-center gap-2 mb-2">
              <DollarSign className="w-5 h-5 text-theme-text-secondary" />
              <h3 className="font-semibold text-theme-text-primary">Revenue</h3>
            </div>
            <div className="text-2xl font-bold text-theme-text-primary">
              {formatCurrency(operationsMetrics.monthlyRevenue)}
            </div>
            <p className="text-sm text-theme-text-secondary">Monthly Revenue</p>
          </div>

          <div className="bg-theme-bg-secondary border-l-4 border-theme-accent p-4 rounded-lg">
            <div className="flex items-center gap-2 mb-2">
              <TrendingUp className="w-5 h-5 text-theme-text-secondary" />
              <h3 className="font-semibold text-theme-text-primary">Profit Margin</h3>
            </div>
            <div className="text-2xl font-bold text-theme-text-primary">
              {operationsMetrics.profitMargin}%
            </div>
            <p className="text-sm text-theme-text-secondary">Average Margin</p>
          </div>

          <div className="bg-theme-bg-secondary border-l-4 border-theme-border p-4 rounded-lg">
            <div className="flex items-center gap-2 mb-2">
              <ShoppingCart className="w-5 h-5 text-theme-text-secondary" />
              <h3 className="font-semibold text-theme-text-primary">Sales</h3>
            </div>
            <div className="text-2xl font-bold text-theme-text-primary">
              {operationsMetrics.monthlySales.toLocaleString()} units
            </div>
            <p className="text-sm text-theme-text-secondary">Monthly Sales</p>
          </div>

          <div className="bg-theme-bg-secondary border-l-4 border-theme-warning p-4 rounded-lg">
            <div className="flex items-center gap-2 mb-2">
              <Activity className="w-5 h-5 text-theme-text-secondary" />
              <h3 className="font-semibold text-theme-text-primary">Growth Rate</h3>
            </div>
            <div className="text-2xl font-bold text-theme-text-primary">
              {operationsMetrics.growthRate}%
            </div>
            <p className="text-sm text-theme-text-secondary">Month over Month</p>
          </div>
        </div>

        {/* Strategic Metrics */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="bg-theme-bg-secondary border-l-4 border-theme-accent p-4 rounded-lg">
            <div className="flex items-center gap-2 mb-2">
              <BarChart2 className="w-5 h-5 text-theme-text-secondary" />
              <h3 className="font-semibold text-theme-text-primary">Market Position</h3>
            </div>
            <div className="text-2xl font-bold text-theme-text-primary"><PlaceholderText text="Strong" /></div>
            <p className="text-sm text-theme-text-secondary">{((companyState.marketShare || 0) * 100).toFixed(1)}% market share</p>
          </div>

          <div className="bg-theme-bg-secondary border-l-4 border-theme-warning p-4 rounded-lg">
            <div className="flex items-center gap-2 mb-2">
              <Building2 className="w-5 h-5 text-theme-text-secondary" />
              <h3 className="font-semibold text-theme-text-primary">Company Value</h3>
            </div>
            <div className="text-2xl font-bold text-theme-text-primary">
              {formatCurrency(operationsMetrics.companyValue)}
            </div>
            <p className="text-sm text-theme-text-secondary">Total Valuation</p>
          </div>
        </div>

        {/* Resource Management */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          <div className="bg-theme-bg-secondary border-l-4 border-theme-accent p-4 rounded-lg">
            <div className="flex items-center gap-2 mb-2">
              <Factory className="w-5 h-5 text-theme-text-secondary" />
              <h3 className="font-semibold text-theme-text-primary">Manufacturing</h3>
            </div>
            <div className="text-2xl font-bold text-theme-text-primary"><PlaceholderText text="85%" /></div>
            <p className="text-sm text-theme-text-secondary">Capacity Utilization</p>
          </div>

          <div className="bg-theme-bg-secondary border-l-4 border-theme-border p-4 rounded-lg">
            <div className="flex items-center gap-2 mb-2">
              <Users className="w-5 h-5 text-theme-text-secondary" />
              <h3 className="font-semibold text-theme-text-primary">Workforce</h3>
            </div>
            <div className="text-2xl font-bold text-theme-text-primary">
              {operationsMetrics.employeeCount}
            </div>
            <p className="text-sm text-theme-text-secondary">Total Employees</p>
          </div>

          <div className="bg-theme-bg-secondary border-l-4 border-theme-success p-4 rounded-lg">
            <div className="flex items-center gap-2 mb-2">
              <Truck className="w-5 h-5 text-theme-text-secondary" />
              <h3 className="font-semibold text-theme-text-primary">Supply Chain</h3>
            </div>
            <div className="text-2xl font-bold text-theme-text-primary"><PlaceholderText text="94%" /></div>
            <p className="text-sm text-theme-text-secondary">Logistics Efficiency</p>
          </div>

          <div className="bg-theme-bg-secondary border-l-4 border-theme-warning p-4 rounded-lg">
            <div className="flex items-center gap-2 mb-2">
              <Store className="w-5 h-5 text-theme-text-secondary" />
              <h3 className="font-semibold text-theme-text-primary">Retail</h3>
            </div>
            <div className="text-2xl font-bold text-theme-text-primary"><PlaceholderText text="128" /></div>
            <p className="text-sm text-theme-text-secondary">Store Locations</p>
          </div>
        </div>

        {/* Market Conditions */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          <div className="bg-theme-bg-secondary border-l-4 border-theme-accent p-4 rounded-lg">
            <div className="flex items-center gap-2 mb-2">
              <TrendingUp className="w-5 h-5 text-theme-text-secondary" />
              <h3 className="font-semibold text-theme-text-primary">Economic Cycle</h3>
            </div>
            <div className="text-2xl font-bold text-theme-text-primary"><PlaceholderText text="Growth" /></div>
            <p className="text-sm text-theme-text-secondary">Accelerating</p>
          </div>

          <div className="bg-theme-bg-secondary border-l-4 border-theme-border p-4 rounded-lg">
            <div className="flex items-center gap-2 mb-2">
              <Activity className="w-5 h-5 text-theme-text-secondary" />
              <h3 className="font-semibold text-theme-text-primary">Market Activity</h3>
            </div>
            <div className="text-2xl font-bold text-theme-text-primary"><PlaceholderText text="High" /></div>
            <p className="text-sm text-theme-text-secondary">Strong consumer demand</p>
          </div>

          <div className="bg-theme-bg-secondary border-l-4 border-theme-success p-4 rounded-lg">
            <div className="flex items-center gap-2 mb-2">
              <Calendar className="w-5 h-5 text-theme-text-secondary" />
              <h3 className="font-semibold text-theme-text-primary">Seasonal Effect</h3>
            </div>
            <div className="text-2xl font-bold text-theme-text-primary"><PlaceholderText text="15%" /></div>
            <p className="text-sm text-theme-text-secondary">Holiday Season</p>
          </div>

          <div className="bg-theme-bg-secondary border-l-4 border-theme-warning p-4 rounded-lg">
            <div className="flex items-center gap-2 mb-2">
              <Globe className="w-5 h-5 text-theme-text-secondary" />
              <h3 className="font-semibold text-theme-text-primary">Global Events</h3>
            </div>
            <div className="text-2xl font-bold text-theme-text-primary"><PlaceholderText text="3 Active" /></div>
            <p className="text-sm text-theme-text-secondary">Market Influences</p>
          </div>
        </div>

        {/* Cost Breakdown */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="bg-theme-bg-secondary border-l-4 border-theme-accent p-4 rounded-lg">
            <h3 className="font-semibold text-theme-text-primary mb-2">Monthly Operations Cost</h3>
            <p className="text-2xl font-bold text-theme-text-primary">
              {formatCurrency(operationsMetrics.monthlyOperationsCost)}
            </p>
            <p className="text-sm text-theme-text-secondary mt-1">
              Total operational expenses
            </p>
          </div>
          
          <div className="bg-theme-bg-secondary border-l-4 border-theme-border p-4 rounded-lg">
            <h3 className="font-semibold text-theme-text-primary mb-2">Employee Salaries</h3>
            <p className="text-2xl font-bold text-theme-text-primary">
              {formatCurrency(operationsMetrics.monthlySalaryCost)}
            </p>
            <p className="text-sm text-theme-text-secondary mt-1">
              {operationsMetrics.employeeCount} employees
            </p>
          </div>
          
          <div className="bg-theme-bg-secondary border-l-4 border-theme-success p-4 rounded-lg">
            <h3 className="font-semibold text-theme-text-primary mb-2">Overhead Costs</h3>
            <p className="text-2xl font-bold text-theme-text-primary">
              {formatCurrency(operationsMetrics.overheadCost)}
            </p>
            <p className="text-sm text-theme-text-secondary mt-1">
              Infrastructure & utilities
            </p>
          </div>
        </div>
      </div>

      {/* Research Section */}
      <div className="space-y-4 mt-6">
        <h3 className="text-lg font-semibold text-theme-text-secondary">Research</h3>
        
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          <div className="bg-theme-bg-secondary border-l-4 border-theme-accent p-4 rounded-lg">
            <div className="flex items-center gap-2 mb-2">
              <TrendingUp className="w-5 h-5 text-theme-text-secondary" />
              <h3 className="font-semibold text-theme-text-primary">Research Points</h3>
            </div>
            <div className="text-2xl font-bold text-theme-text-primary">
              {researchMetrics.researchPoints.toLocaleString()} RP
            </div>
            <p className="text-sm text-theme-text-secondary">Current Balance</p>
          </div>

          <div className="bg-theme-bg-secondary border-l-4 border-theme-border p-4 rounded-lg">
            <div className="flex items-center gap-2 mb-2">
              <DollarSign className="w-5 h-5 text-theme-text-secondary" />
              <h3 className="font-semibold text-theme-text-primary">Monthly R&D</h3>
            </div>
            <div className="text-2xl font-bold text-theme-text-primary">
              {formatCurrency(researchMetrics.monthlyRD)}
            </div>
            <p className="text-sm text-theme-text-secondary">Investment Budget</p>
          </div>

          <div className="bg-theme-bg-secondary border-l-4 border-theme-success p-4 rounded-lg">
            <div className="flex items-center gap-2 mb-2">
              <Activity className="w-5 h-5 text-theme-text-secondary" />
              <h3 className="font-semibold text-theme-text-primary">RP Generation</h3>
            </div>
            <div className="text-2xl font-bold text-theme-text-primary">
              {researchMetrics.rpGeneration.toFixed(1)} RP/month
            </div>
            <p className="text-sm text-theme-text-secondary">Total Generation Rate</p>
          </div>

          <div className="bg-theme-bg-secondary border-l-4 border-theme-warning p-4 rounded-lg">
            <div className="flex items-center gap-2 mb-2">
              <TrendingDown className="w-5 h-5 text-theme-text-secondary" />
              <h3 className="font-semibold text-theme-text-primary">Cost Reduction</h3>
            </div>
            <div className="text-2xl font-bold text-theme-text-primary">
              {researchMetrics.costReduction}%
            </div>
            <p className="text-sm text-theme-text-secondary">From Research</p>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          <div className="bg-theme-bg-secondary border-l-4 border-theme-accent p-4 rounded-lg">
            <div className="flex items-center gap-2 mb-2">
              <Factory className="w-5 h-5 text-theme-text-secondary" />
              <h3 className="font-semibold text-theme-text-primary">Efficiency</h3>
            </div>
            <div className="text-2xl font-bold text-theme-text-primary">
              {researchMetrics.efficiency}%
            </div>
            <p className="text-sm text-theme-text-secondary">Process Optimization</p>
          </div>

          <div className="bg-theme-bg-secondary border-l-4 border-theme-accent p-4 rounded-lg">
            <div className="flex items-center gap-2 mb-2">
              <DollarSign className="w-5 h-5 text-theme-text-secondary" />
              <h3 className="font-semibold text-theme-text-primary">Monthly R&D Investment</h3>
            </div>
            <div className="text-2xl font-bold text-theme-text-primary">
              {formatCurrency(researchMetrics.monthlyRDInvestment)}
            </div>
            <p className="text-sm text-theme-text-secondary">Direct Funding</p>
          </div>

          <div className="bg-theme-bg-secondary border-l-4 border-theme-error p-4 rounded-lg">
            <div className="flex items-center gap-2 mb-2">
              <TrendingUp className="w-5 h-5 text-theme-text-secondary" />
              <h3 className="font-semibold text-theme-text-primary">Research Points Spent</h3>
            </div>
            <div className="text-2xl font-bold text-theme-text-primary">
              {researchMetrics.researchPointsSpent.toLocaleString()} RP
            </div>
            <p className="text-sm text-theme-text-secondary">Total Invested</p>
          </div>

          <div className="bg-theme-bg-secondary border-l-4 border-theme-warning p-4 rounded-lg">
            <div className="flex items-center gap-2 mb-2">
              <Activity className="w-5 h-5 text-theme-text-secondary" />
              <h3 className="font-semibold text-theme-text-primary">Cost Efficiency</h3>
            </div>
            <div className="text-2xl font-bold text-theme-text-primary">
              ${researchMetrics.costEfficiency.toFixed(2)}/RP
            </div>
            <p className="text-sm text-theme-text-secondary">Investment per RP</p>
          </div>
        </div>
      </div>

      {/* Marketing Section */}
      <div className="space-y-4 mt-6">
        <h3 className="text-lg font-semibold text-theme-text-secondary">Marketing</h3>
        
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="bg-theme-bg-secondary border-l-4 border-theme-accent p-4 rounded-lg">
            <div className="flex items-center gap-2 mb-2">
              <DollarSign className="w-5 h-5 text-theme-text-secondary" />
              <h3 className="font-semibold text-theme-text-primary">Monthly Marketing Budget</h3>
            </div>
            <div className="text-2xl font-bold text-theme-text-primary">
              {formatCurrency(marketingMetrics.monthlyMarketingBudget)}
            </div>
            <p className="text-sm text-theme-text-secondary"><PlaceholderText text="15% of revenue" /></p>
          </div>

          <div className="bg-theme-bg-secondary border-l-4 border-theme-border p-4 rounded-lg">
            <div className="flex items-center gap-2 mb-2">
              <TrendingUp className="w-5 h-5 text-theme-text-secondary" />
              <h3 className="font-semibold text-theme-text-primary">Marketing Efficiency</h3>
            </div>
            <div className="text-2xl font-bold text-theme-text-primary">
              ${marketingMetrics.marketingEfficiency.toFixed(2)}
            </div>
            <p className="text-sm text-theme-text-secondary">Revenue per marketing dollar</p>
          </div>

          <div className="bg-theme-bg-secondary border-l-4 border-theme-success p-4 rounded-lg">
            <div className="flex items-center gap-2 mb-2">
              <Building2 className="w-5 h-5 text-theme-text-secondary" />
              <h3 className="font-semibold text-theme-text-primary">Brand Value</h3>
            </div>
            <div className="text-2xl font-bold text-theme-text-primary">
              {formatCurrency(marketingMetrics.brandValue)}
            </div>
            <p className="text-sm text-theme-text-secondary">Based on reputation</p>
          </div>
        </div>
      </div>

      {/* Finance Section */}
      <div className="space-y-4 mt-6">
        <h3 className="text-lg font-semibold text-theme-text-secondary">Finance</h3>
        
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          <div className="bg-theme-bg-secondary border-l-4 border-theme-success p-4 rounded-lg">
            <div className="flex items-center gap-2 mb-2">
              <DollarSign className="w-5 h-5 text-theme-text-secondary" />
              <h3 className="font-semibold text-theme-text-primary">Monthly Revenue</h3>
            </div>
            <div className="text-2xl font-bold text-theme-text-primary">
              {formatCurrency(financeMetrics.monthlyRevenue)}
            </div>
            <p className="text-sm text-theme-text-secondary">Total Income</p>
          </div>

          <div className="bg-theme-bg-secondary border-l-4 border-theme-accent p-4 rounded-lg">
            <div className="flex items-center gap-2 mb-2">
              <TrendingUp className="w-5 h-5 text-theme-text-secondary" />
              <h3 className="font-semibold text-theme-text-primary">Profit Margin</h3>
            </div>
            <div className="text-2xl font-bold text-theme-text-primary">
              {financeMetrics.profitMargin.toFixed(1)}%
            </div>
            <p className="text-sm text-theme-text-secondary">Net Margin</p>
          </div>

          <div className="bg-theme-bg-secondary border-l-4 border-theme-error p-4 rounded-lg">
            <div className="flex items-center gap-2 mb-2">
              <TrendingDown className="w-5 h-5 text-theme-text-secondary" />
              <h3 className="font-semibold text-theme-text-primary">Monthly Costs</h3>
            </div>
            <div className="text-2xl font-bold text-theme-text-primary">
              {formatCurrency(financeMetrics.monthlyCosts)}
            </div>
            <p className="text-sm text-theme-text-secondary">Total Expenses</p>
          </div>

          <div className="bg-theme-bg-secondary border-l-4 border-theme-border p-4 rounded-lg">
            <div className="flex items-center gap-2 mb-2">
              <Activity className="w-5 h-5 text-theme-text-secondary" />
              <h3 className="font-semibold text-theme-text-primary">Net Profit</h3>
            </div>
            <div className={`text-2xl font-bold ${financeMetrics.netProfit >= 0 ? 'text-theme-text-primary' : 'text-theme-text-primary'}`}>
              {formatCurrency(financeMetrics.netProfit)}
            </div>
            <p className="text-sm text-theme-text-secondary">Monthly Net</p>
          </div>
        </div>
      </div>
    </div>
  );
};